import React, { useCallback, useEffect, useState } from 'react';
import { T } from '@testout/labsim-ui-atomics';
import LabSimService from '../../services/LabSimService';
import styles from './StudentDetailDialog.module.less';
import { SyncGradesInfo } from './ManualSyncDialog';
function formatDate(isoDate, withTime = false) {
    if (!isoDate) {
        return '—';
    }
    if (withTime) {
        const d = new Date(isoDate);
        const datePart = d.toLocaleDateString('en-US', {
            year: 'numeric',
            month: '2-digit',
            day: '2-digit',
        });
        const timePart = d.toLocaleTimeString('en-US', {
            hour: '2-digit',
            minute: '2-digit',
            hour12: true,
        });
        return `${datePart} ${timePart}`;
    }
    return new Date(isoDate).toLocaleString('en-US', {
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
    });
}
function dateOrNotSet(isoDate) {
    if (!isoDate) {
        return 'Not set';
    }
    return formatDate(isoDate);
}
function assignmentDotClass(status) {
    if (status === 'Success') {
        return styles.dotSuccess;
    }
    if (status === 'Fail (Latest Sync)') {
        return styles.dotFailLatest;
    }
    return styles.dotFail;
}
function auditDotClass(isSuccess) {
    if (isSuccess) {
        return styles.dotSuccess;
    }
    return styles.dotFail;
}
function auditStatusLabel(isSuccess) {
    if (isSuccess) {
        return 'Success';
    }
    return 'Fail';
}
function auditMessageText(entry) {
    var _a, _b;
    return (_b = (_a = entry.errorMessage) !== null && _a !== void 0 ? _a : entry.errorCode) !== null && _b !== void 0 ? _b : '';
}
function AuditEntryRow({ entry }) {
    return (React.createElement("div", { className: styles.auditRow },
        React.createElement("span", { className: styles.auditColStatus },
            React.createElement("div", { className: `${styles.statusDot} ${auditDotClass(entry.isSuccess)}`, "aria-hidden": 'true' }),
            auditStatusLabel(entry.isSuccess)),
        React.createElement("span", { className: styles.auditColMessage }, auditMessageText(entry)),
        React.createElement("span", { className: styles.auditColTimestamp }, formatDate(`${entry.timestamp}`, true))));
}
function AssignmentRow({ assignment, isExpanded, onToggle, }) {
    const handleClick = useCallback(() => {
        onToggle(assignment.lineItemId);
    }, [onToggle, assignment.lineItemId]);
    return (React.createElement(T.Accordion.Section, { automationId: `assignment-${assignment.lineItemId}`, expanded: isExpanded, onClick: handleClick, className: styles.assignmentSection },
        React.createElement(T.Accordion.Section.Header, { className: styles.assignmentHeader },
            React.createElement(T.Accordion.Section.Title, { className: styles.assignmentTitle }, assignment.label),
            React.createElement("span", { className: styles.statusBadge },
                React.createElement("div", { className: `${styles.statusDot} ${assignmentDotClass(assignment.status)}`, "aria-hidden": 'true' }),
                React.createElement("span", { className: styles.statusLabel }, assignment.status)),
            React.createElement(T.Accordion.Section.Indicator, null)),
        React.createElement(T.Accordion.Body, null,
            React.createElement("div", { className: styles.assignmentDetails },
                React.createElement("div", { className: styles.assignmentDates },
                    React.createElement("div", null,
                        React.createElement("strong", null, "Available: "),
                        dateOrNotSet(assignment.startAtUtc)),
                    React.createElement("div", null,
                        React.createElement("strong", null, "Due: "),
                        dateOrNotSet(assignment.endAtUtc))),
                assignment.auditEntries.length > 0 && (React.createElement("div", { className: styles.auditTable }, assignment.auditEntries.map((entry, idx) => (React.createElement(AuditEntryRow, { key: idx, entry: entry })))))))));
}
function AssignmentListContent({ loading, assignments, expandedId, onToggle, }) {
    if (loading) {
        return React.createElement(T.Loading.Inline, null);
    }
    if (assignments.length === 0) {
        return (React.createElement(T.Text.P, { automationId: 'noSyncHistory', className: styles.noSyncHistory }, "No sync history available."));
    }
    return (React.createElement(T.Accordion, { automationId: 'assignmentList', variation: 'collapsed', className: styles.studentDetailAccordion }, assignments.map((a) => (React.createElement(AssignmentRow, { key: a.lineItemId, assignment: a, isExpanded: expandedId === a.lineItemId, onToggle: onToggle })))));
}
function StudentDetailDialog({ isOpen, studentId, studentName, classProfileId, className, classStartDate, classEndDate, onClose, goBack, }) {
    const [assignments, setAssignments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [expandedId, setExpandedId] = useState(null);
    const [syncing, setSyncing] = useState(false);
    const [syncToast, setSyncToast] = useState(null);
    useEffect(() => {
        if (!isOpen || !studentId || !classProfileId) {
            return;
        }
        setLoading(true);
        setAssignments([]);
        setExpandedId(null);
        setSyncToast(null);
        LabSimService.getStudentAssignments(studentId, classProfileId)
            .then((data) => {
            var _a;
            setAssignments((_a = data.assignments) !== null && _a !== void 0 ? _a : []);
            setLoading(false);
        })
            .catch(() => {
            setLoading(false);
        });
    }, [isOpen, studentId, classProfileId]);
    const handleToggle = useCallback((lineItemId) => {
        setExpandedId((prev) => {
            if (prev === lineItemId) {
                return null;
            }
            return lineItemId;
        });
    }, []);
    const handleSyncGrades = useCallback(() => {
        setSyncing(true);
        LabSimService.syncStudentGrades(studentId, classProfileId)
            .then(() => {
            setSyncing(false);
            setSyncToast('queued');
        })
            .catch(() => {
            setSyncing(false);
            setSyncToast('error');
        });
    }, [studentId, classProfileId]);
    const handleSyncToastHidden = useCallback(() => {
        setSyncToast(null);
    }, []);
    if (!isOpen) {
        return null;
    }
    const classDateRange = `${formatDate(classStartDate)} – ${formatDate(classEndDate)}`;
    return (React.createElement(T.Dialog, { automationId: 'studentDetailDialog', onClose: onClose, className: styles.studentDetailDialog },
        React.createElement(T.Dialog.Header, null,
            React.createElement(T.Dialog.TitleBar, null,
                React.createElement(T.Dialog.TitleBar.Actions, { position: 'left' },
                    React.createElement(T.IconButton, { screenReaderDescription: 'Back', automationId: 'backText', iconVariation: T.IconVariation.ArrowLeft, onClick: goBack })),
                React.createElement(T.Dialog.Title, null, "Sync Grades for Student"),
                React.createElement(T.Dialog.TitleBar.Actions, { position: 'right' },
                    React.createElement(T.Dialog.CloseButton, null))),
            React.createElement(T.Dialog.SubTitle, null,
                React.createElement("div", { className: styles.studentDetailHeader },
                    React.createElement("span", { className: styles.headerItem },
                        React.createElement(T.Icon, { automationId: 'studentIcon', variation: T.IconVariation.Profile, className: styles.headerIcon }),
                        React.createElement(T.Text.Span, { automationId: 'studentDetailName' }, studentName)),
                    React.createElement("span", { className: styles.headerPipe, "aria-hidden": 'true' }, "|"),
                    React.createElement("span", { className: styles.headerItem },
                        React.createElement(T.Icon, { automationId: 'classIcon', variation: T.IconVariation.School, className: styles.headerIcon }),
                        React.createElement(T.Text.Span, { automationId: 'studentDetailClassName' }, className)),
                    React.createElement("span", { className: styles.headerPipe, "aria-hidden": 'true' }, "|"),
                    React.createElement("span", { className: styles.headerItem },
                        React.createElement(T.Icon, { automationId: 'calendarIcon', variation: T.IconVariation.Calendar, className: styles.headerIcon }),
                        React.createElement(T.Text.Span, { automationId: 'studentDetailDateRange', className: styles.studentDetailDates }, classDateRange))))),
        React.createElement(T.Dialog.Body, null,
            React.createElement("div", { className: styles.studentDetailBody },
                React.createElement(SyncGradesInfo, null)),
            React.createElement("div", { className: styles.syncActions },
                React.createElement(T.Button, { automationId: 'syncAllGrades', onClick: handleSyncGrades, disabled: syncing || assignments.length === 0, variation: T.ButtonVariation.Secondary },
                    React.createElement(T.IconText, { automationId: 'syncAllText', variation: T.Text.Variation.Inherit, leftIconVariation: T.IconVariation.Sync }, "Sync Grades")),
                React.createElement(T.Popover, { automationId: 'syncOptionsPopover' },
                    React.createElement(T.Popover.Trigger, null,
                        React.createElement(T.Button, { automationId: 'syncOptions', variation: T.ButtonVariation.SecondaryText },
                            React.createElement(T.Icon, { automationId: 'syncOptionsInfoIcon', variation: T.IconVariation.InfoCircle }))),
                    React.createElement(T.Popover.Content, { className: styles.popoverContent },
                        React.createElement(T.Text.P, { automationId: 'success' },
                            React.createElement(T.Text.Span, { automationId: 'successLabel', className: styles.popoverTextLabel }, "Success:\u00A0"),
                            "Grades synced successfully."),
                        React.createElement(T.Text.P, { automationId: 'fail' },
                            React.createElement(T.Text.Span, { automationId: 'failLabel', className: styles.popoverTextLabel }, "Fail:\u00A0"),
                            "Grades were not synced."),
                        React.createElement(T.Text.P, { automationId: 'fail-latest-sync' },
                            React.createElement(T.Text.Span, { automationId: 'failLatestSyncLabel', className: styles.popoverTextLabel }, "Fail (Latest Sync):\u00A0"),
                            "Grades were not synced during the most recent syncing attempt, even if scores had successfully synced in the past.")))),
            React.createElement(AssignmentListContent, { loading: loading, assignments: assignments, expandedId: expandedId, onToggle: handleToggle }),
            syncToast === 'queued' && (React.createElement(T.SimpleToast, { onHidden: handleSyncToastHidden }, "Grade sync queued. Sync can take several minutes. Please check back later.")),
            syncToast === 'error' && (React.createElement(T.SimpleToast, { onHidden: handleSyncToastHidden }, "Grade sync could not be queued. Please try again."))),
        React.createElement(T.Dialog.Buttons, null,
            React.createElement(T.Button, { automationId: 'goBackToManualSyncDialog', variation: T.ButtonVariation.Secondary, onClick: goBack }, "Back"))));
}
export default StudentDetailDialog;
//# sourceMappingURL=StudentDetailDialog.js.map